import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { SimpleFAB } from '@/design-system/components/actions/FAB';
import { AttentionBanner } from '@/design-system/components/feedback/AttentionBanner';
import { BlockingLoadingOverlay } from '@/design-system/components/feedback/BlockingLoadingOverlay';
import { Sparkles } from 'lucide-react';
import { FeedTab } from '@/features/memories/components/FeedTab';
import { Photo, PhotoBatch } from '@/types';
import { useBaulesStore } from '@/store/useBaulesStore';
import { usePersonasStore } from '@/store/usePersonasStore';
import { useRecuerdosStore } from '@/store/useRecuerdosStore';
import { useAppConfigStore } from '@/store/useAppConfigStore';
import { useUIStore } from '@/store/uiStore';
import { loadBaulFeed, loadMoreBaulFeed } from '@/features/memories/useCases';
import { loadChapterPhotos } from '@/features/photos/useCases';
import { openPhotoViewer, photoViewerPath } from '@/features/photos/viewerNavigation';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { getBaulPermissions } from '@/utils/roleUtils';
import { countPendingInvites } from '@/utils/personaOrder';
import { useRecuerdoActions } from './useRecuerdoActions';

interface BaulFeedTabContainerProps {
  baulId: string;
  baulName: string;
  // Reutiliza handleSelectChapter de BaulRoute (compartido con la pestaña de capítulos) en
  // vez de duplicar aquí su lógica de cargar-y-navegar — a diferencia de abrir una foto
  // (más abajo), esta acción sí tiene un caller que ya la necesita para otra cosa.
  onOpenChapter?: (chapterId: string) => void;
}

// Lazily loads and reads the merged baulFeed (recuerdos + photo-upload-batch cards, sorted
// server-side by BaulFeedManager) — a genuinely new endpoint, not just a client-side filter,
// per the API-conventions "no breaking changes" rule.
export function BaulFeedTabContainer({ baulId, baulName, onOpenChapter }: BaulFeedTabContainerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { baulFeed, baulFeedHasMore } = useRecuerdosStore();
  const chatEnabled = useAppConfigStore((state) => state.chatEnabled);
  const sharedLinksEnabled = useAppConfigStore((state) => state.sharedLinksEnabled);
  const { editRecuerdo, shareRecuerdo } = useRecuerdoActions(baulName);
  const { run, isPending } = useAsyncAction();

  // "Invitar a la familia" attention banner: only for someone who can actually send invites,
  // and only while there's still someone to invite — see countPendingInvites/AttentionBanner
  // (uiStore) for what "pending" and "dismissed" mean here.
  const baul = useBaulesStore((state) => state.baules.find((b) => b.id === baulId));
  const personas = usePersonasStore((state) => state.personas[baulId]) || [];
  const isInviteBannerDismissed = useUIStore((state) => state.isInviteBannerDismissed(baulId));
  const dismissInviteBanner = useUIStore((state) => state.dismissInviteBanner);
  const pendingInvitesCount = countPendingInvites(personas);
  const showInviteBanner = getBaulPermissions(baul).canManageBaulInvite && pendingInvitesCount > 0 && !isInviteBannerDismissed;
  // Tracks how many handleOpenPhoto() calls are in flight, independent of useAsyncAction's
  // per-photo keys (see below) — the overlay just needs "is some photo opening right now",
  // not which one.
  const [openingPhotoCount, setOpeningPhotoCount] = useState(0);

  useEffect(() => {
    if (auth.isAuthenticated && !baulFeed[baulId]) {
      run(() => loadBaulFeed(baulId), { key: 'baul-feed', errorMessage: 'Error al cargar el feed' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, baulId, baulFeed]);

  const feedItems = baulFeed[baulId] || [];

  // 'baul-feed-more' es una key propia (distinta de 'baul-feed', la carga inicial) para que
  // cargar la siguiente página no dispare el overlay a pantalla completa, solo el spinner en
  // línea al final de la lista (ver FeedTab).
  const handleLoadMore = () => {
    if (!baulFeedHasMore[baulId]) return;
    run(() => loadMoreBaulFeed(baulId), { key: 'baul-feed-more', errorMessage: 'Error al cargar más' });
  };

  const handleUserClick = (personaId: string) => {
    navigate(`/baules/${baulId}/personas/${personaId}`, { state: { returnTab: 'recuerdos' } });
  };

  const handleOpenPhoto = async (photoId: string, chapterId?: string) => {
    // Una foto suelta no tiene chapterId: sus fotos ya están cargadas por el efecto de
    // inicialización de BaulRoute (loadLoosePhotos), así que no hace falta cargar nada
    // antes de navegar.
    if (!chapterId) {
      openPhotoViewer(navigate, location, photoViewerPath(`/baules/${baulId}/fotos-sueltas`, photoId));
      return;
    }

    // Uniquely keyed per call — two feed entries can point at the same photo (e.g. a recuerdo
    // about a photo, and its "photo uploaded" entry, both inheriting the same chapterId), so
    // every click must trigger its own load/navigation attempt rather than sharing
    // useAsyncAction's implicit default key (or one derived from photoId/chapterId, which would
    // collide in exactly this duplicate-entry case) — that would silently drop a second click
    // landing while an earlier one is still in flight. loadChapterPhotos() is a safe, idempotent
    // read, so letting concurrent calls for the same chapter overlap has no downside.
    setOpeningPhotoCount((count) => count + 1);
    try {
      const result = await run(() => loadChapterPhotos(chapterId), {
        key: `open-photo-${crypto.randomUUID()}`,
        errorMessage: 'Error al cargar las fotos',
      });
      if (result.ok) openPhotoViewer(navigate, location, photoViewerPath(`/baules/${baulId}/capitulos/${chapterId}`, photoId));
    } finally {
      setOpeningPhotoCount((count) => count - 1);
    }
  };

  // A diferencia de una foto de recuerdo (puede vivir en un capítulo cuyas fotos no estén
  // cargadas todavía), las fotos de un lote siempre se sirven desde su propio endpoint
  // acotado — no hace falta cargar nada antes de navegar, PhotoBatchViewerRoute las carga él
  // mismo si hace falta.
  const handleOpenBatchPhoto = (batch: PhotoBatch, photo: Photo) => {
    openPhotoViewer(navigate, location, photoViewerPath(`/baules/${baulId}/subida/${batch.batchId}`, photo.id));
  };

  const handleOpenBatchGrid = (batch: PhotoBatch) => {
    navigate(`/baules/${baulId}/subida/${batch.batchId}`);
  };

  const pendingInvitesMessage = pendingInvitesCount === 1
    ? 'Aún hay 1 persona en tu familia que no está invitada.'
    : `Aún hay ${pendingInvitesCount} personas en tu familia que no están invitadas.`;

  return (
    <>
      {showInviteBanner && (
        <AttentionBanner
          message={pendingInvitesMessage}
          ctaLabel="Invitar"
          dismissLabel="Ocultar"
          onCta={() => navigate(`/baules/${baulId}/invitar`)}
          onDismiss={() => dismissInviteBanner(baulId)}
          className="mb-4"
        />
      )}
      <FeedTab
        feedItems={feedItems}
        onOpenChapter={onOpenChapter}
        onOpenPhoto={handleOpenPhoto}
        onUserClick={handleUserClick}
        onShareRecuerdo={sharedLinksEnabled ? shareRecuerdo : undefined}
        onEditRecuerdo={editRecuerdo}
        onOpenBatchPhoto={handleOpenBatchPhoto}
        onOpenBatchGrid={handleOpenBatchGrid}
        onLoadMore={handleLoadMore}
        hasMore={baulFeedHasMore[baulId] ?? false}
        isLoadingMore={isPending('baul-feed-more')}
      />
      <SimpleFAB
        label="Recordemos juntos"
        icon={<Sparkles className="w-5 h-5" />}
        onClick={() => navigate(`/baules/${baulId}/recordar`)}
        hidden={!chatEnabled}
      />
      {openingPhotoCount > 0 && <BlockingLoadingOverlay message="Cargando fotos..." />}
    </>
  );
}
